import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * A4 任務與系統（`GET /api/admin/runs`＋`GET /api/admin/system`，見 docs/DESIGN-admin.md §3 A4／§4.3-2、3）。
 *
 * 版面（照 §3 A4 的順序）：
 *   1. 註冊工具列：站別／顆數＋「註冊一批」。**冷卻中 disabled、顯示剩餘秒數、不發請求**（§6.2-4）。
 *   2. 註冊批次歷史：桌機 7 欄表格（時間／來源／站別／要求／成功／耗時／池子結餘），
 *      手機轉卡片；點一列開抽屜看逐顆結果與失敗原因。**刻意不做「重跑這一輪」**
 *      （§3 A4：避免誤觸打站，重跑走排程或帳號池的註冊按鈕，那裡有 20 秒節流）。
 *   3. 系統 4 卡：註冊冷卻／relay／站方可達性／排程（手機 2×2、桌機 4 欄）。
 *
 * 三個必須遵守的行為約束：
 *   - **站方檢查不要每次頁面載入都打**（§4.3-3）：掛載時只呼叫 `system({refresh:false})`
 *     （後端有 60 秒快取），只有人工按「重新檢查」才 `refresh:true` 強制刷新。
 *   - **不要連點重複發請求**：`checkingRef`／`registeringRef` 擋在 setState 之前——
 *     state 是非同步的，連點兩下會在 state 更新前都通過判斷；按鈕同時 disabled 並帶 `aria-busy`。
 *   - **冷卻的權威來源是後端**（`registration.throttleRemainingMs`），但按下註冊後本地立刻用
 *     `POOL_REGISTER_GAP_MS` 起算，不等 `system` 回來——否則那幾百毫秒的空窗就足以連點第二批。
 *
 * 邊界：這個檔只依賴 `ApiClient`（不 fetch、不看 `config.json`、不碰其他分頁）。
 * `listRuns`／`system`／`registerAccountPool` 都是 client 上的方法，缺哪個就顯示「尚未接線」
 * 而不是白畫面（§6.3：舊 mock client 也要開得起來）。
 *
 * 驗收重點（§6.1）：桌機 1280×800 七欄完整可見；手機 390×844 表格轉卡片、4 卡 2×2、
 * 按鈕不被底部 nav 遮住（內容底部預留 `env(safe-area-inset-bottom)`）。
 */
import { enabledServices } from '../../platforms';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SERVICE_LABELS, MAX_POOL_REGISTER, POOL_REGISTER_GAP_MS, } from '../../types';
import { cooldownSecondsLeft } from '../PoolPanel';
import { EntityTable } from '../EntityTable';
/** 一頁抓幾筆（§3 A4：預設最新 20 筆）。 */
const RUN_PAGE_SIZE = 20;
/** 960px 以下轉卡片：七欄在 390 一定爆版，在 768 也只剩 80px 一欄。 */
const NARROW_MAX_WIDTH = 960;
/** `source` 的中文說法：照 §4.3-2 的白名單（console／grow_pool／manual）。 */
export const RUN_SOURCE_LABELS = {
    console: '控制台',
    grow_pool: '排程',
    manual: '手動',
};
const SCHEDULER_SOURCE_LABELS = {
    cron: '排程',
    manual: '手動',
    none: '未回寫',
};
const SCHEDULER_RESULT_LABELS = {
    ok: '成功',
    failed: '失敗',
    unknown: '未知',
};
/** 排程結果 → 狀態點色階（沿用既有的 `.cui-health-dot--*`）。 */
const RESULT_DOT = {
    ok: 'cui-health-dot--online',
    failed: 'cui-health-dot--down',
    unknown: 'cui-health-dot--limited',
};
const isNum = (value) => typeof value === 'number' && Number.isFinite(value);
function twoDigits(value) {
    return value < 10 ? `0${value}` : String(value);
}
/** 時間戳 → `HH:MM:SS`（非今天帶 `MM-DD`）。壞值給 `—`，不給 `Invalid Date`。 */
export function formatStamp(ts, now = Date.now()) {
    if (!isNum(ts))
        return '—';
    const at = new Date(ts);
    const ref = new Date(now);
    const sameDay = at.getFullYear() === ref.getFullYear() && at.getMonth() === ref.getMonth() && at.getDate() === ref.getDate();
    if (sameDay) {
        return `${twoDigits(at.getHours())}:${twoDigits(at.getMinutes())}:${twoDigits(at.getSeconds())}`;
    }
    return `${twoDigits(at.getMonth() + 1)}-${twoDigits(at.getDate())} ${twoDigits(at.getHours())}:${twoDigits(at.getMinutes())}`;
}
/** 「12 秒前」這種相對時間；給最後檢查時間用，讓人不必自己減。 */
export function formatAgo(ts, now = Date.now()) {
    if (!isNum(ts))
        return '—';
    const diff = Math.max(0, now - ts);
    if (diff < 5_000)
        return '剛剛';
    if (diff < 60_000)
        return `${Math.round(diff / 1000)} 秒前`;
    if (diff < 3_600_000)
        return `${Math.round(diff / 60_000)} 分鐘前`;
    if (diff < 86_400_000)
        return `${Math.round(diff / 3_600_000)} 小時前`;
    return `${Math.round(diff / 86_400_000)} 天前`;
}
/** 耗時：<1 秒給毫秒，否則給到小數一位的秒。 */
export function formatDuration(ms) {
    if (!isNum(ms) || ms < 0)
        return '—';
    return ms < 1000 ? `${Math.round(ms)} ms` : `${(ms / 1000).toFixed(1)} s`;
}
/** 數字或 `—`：未知（`null`／`undefined`）不能畫成 0（§3 共同規則：空與 0 不可混用）。 */
export function countText(value) {
    return isNum(value) ? String(value) : '—';
}
function errorMessage(error) {
    if (error instanceof Error && error.message !== '')
        return error.message;
    if (typeof error === 'string' && error !== '')
        return error;
    return '未知錯誤（沒有訊息）';
}
const HTTP_HINTS = {
    '400': '站方說請求內容不對（多半是表單欄位或密碼規則）',
    '401': '站方要求重新登入（憑證沒被接受）',
    '403': '站方擋住這個請求（WAF／機器人判定）',
    '404': '站方沒有這個端點（站方改版了）',
    '429': '站方限流（等 20 秒再試）',
    '451': '出口 IP 被站方封鎖（換出口再試）',
    '500': '站方伺服器內部錯誤（不是我們的帳號壞了）',
    '502': '出口 relay 沒回應，台灣出口掛了或逾時',
    '503': '站方暫時不可用（維修或被擋）',
    '504': '出口 relay 逾時，出口沒在時限內回應',
};
function describeReasonText(raw) {
    const text = raw.trim();
    if (text === '')
        return '';
    const code = text.match(/(?:^|http[_\s-])(\d{3})\b/i)?.[1];
    if (code !== undefined) {
        const hint = HTTP_HINTS[code] ?? '站方回應非預期狀態';
        return `${hint}（HTTP ${code}）`;
    }
    if (/timeout|timed out|etimedout/i.test(text))
        return '連線逾時（出口或站方沒在時限內回應）';
    if (/fetch failed|network|econnrefused|enotfound|dns/i.test(text))
        return '連不到站方（出口、DNS 或網路中斷）';
    if (/relay/i.test(text))
        return `台灣出口回報失敗：${text}`;
    return text;
}
/**
 * 把一顆的失敗原因翻成看得懂的一句；`null`／空字串也要說清楚，不能只留白。
 * 原始訊息（例如站方回傳的細節）保留在句尾括號，除錯時還查得到。
 */
export function readableRegisterReason(reason) {
    const raw = (reason ?? '').trim();
    if (raw === '')
        return '沒有任何原因（站方與出口都沒回報）';
    const loginFail = raw.match(/^已註冊但登入失敗\s*[:：]\s*(.*)$/);
    if (loginFail) {
        const detail = describeReasonText(loginFail[1] ?? '');
        return `帳號建好了，但登入驗證沒過（可稍後用「重登」）${detail === '' ? '' : `：${detail}`}`;
    }
    return describeReasonText(raw);
}
/** 一批的結算句：「本次 3 顆：成功 1、失敗 2（入池 1）· ek21」。 */
export function batchSummaryText(batch) {
    const ok = batch.results.filter((item) => item.ok).length;
    const bad = batch.results.length - ok;
    const head = `本次 ${batch.results.length} 顆：成功 ${ok}、失敗 ${bad}`;
    // 註冊成功但登入失敗也算入池（有憑證），所以入池數跟成功數可能不同，兩個都寫。
    const pool = batch.created === ok ? '' : `（入池 ${batch.created}）`;
    return `${head}${pool} · ${SERVICE_LABELS[batch.service]}`;
}
/** 逐顆明細：成功與失敗都列出，失敗附可讀原因；`<details>` 可展開／收合。 */
export function RegisterBatches({ batches }) {
    if (batches.length === 0)
        return null;
    return (_jsx("div", { style: { display: 'flex', flexDirection: 'column', gap: 6 }, "data-testid": "admin-ops-register-results", children: batches.map((batch, index) => {
            const ok = batch.results.filter((item) => item.ok).length;
            const bad = batch.results.length - ok;
            return (_jsxs("div", { style: { borderTop: index === 0 ? 'none' : '1px dashed var(--ak-color-border)', paddingTop: index === 0 ? 0 : 6 }, children: [_jsxs("p", { style: { margin: 0, fontSize: 12 }, role: "status", "data-testid": index === 0 ? 'admin-ops-register-summary' : 'admin-ops-register-summary-old', "data-ok": ok, "data-bad": bad, children: [index === 0 ? '' : '上一批 · ', batchSummaryText(batch)] }), _jsxs("details", { open: index === 0, style: { marginTop: 2 }, "data-testid": "admin-ops-register-details", children: [_jsxs("summary", { className: "cui-hint", style: { cursor: 'pointer', fontSize: 11 }, children: ["\u9010\u9846\u660E\u7D30\uFF08", batch.results.length, " \u9846\uFF09", bad > 0 ? ` · 失敗 ${bad} 顆可逐條看原因` : ''] }), _jsx("ul", { className: "cui-pool-results", style: { margin: '4px 0 0', padding: 0, listStyle: 'none' }, children: batch.results.map((item, rowIndex) => (_jsxs("li", { className: `cui-pool-result${item.ok ? ' is-ok' : ' is-bad'}`, "data-testid": "admin-ops-register-result", "data-username": item.username, "data-ok": item.ok ? 'true' : 'false', children: [_jsx("span", { "aria-hidden": "true", children: item.ok ? '✓' : '✗' }), _jsx("span", { className: "cui-mono", children: item.username || '（未回報帳號名）' }), _jsx("span", { className: "cui-pool-result-reason", children: item.ok ? '成功，可用' : readableRegisterReason(item.reason) })] }, `${batch.key}:${item.username}:${rowIndex}`))) })] })] }, batch.key));
        }) }));
}
/** 卡片／表格共用的「可達性」說法。 */
function reachLabel(configured, reachable) {
    if (!configured)
        return '未設定';
    return reachable ? '可達' : '不可達';
}
/** 分頁容器是 CSS 驅動的（外殼沒有 Tailwind 掃描保證），所以斷點在 JS 判斷。 */
function useNarrowLayout(maxWidth = NARROW_MAX_WIDTH) {
    const query = `(max-width: ${maxWidth}px)`;
    const [narrow, setNarrow] = useState(() => typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(query).matches);
    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function')
            return;
        const mq = window.matchMedia(query);
        const onChange = () => setNarrow(mq.matches);
        onChange();
        mq.addEventListener('change', onChange);
        return () => mq.removeEventListener('change', onChange);
    }, [query]);
    return narrow;
}
/* ── 樣式（inline：console-ui 不假設宿主的 Tailwind 有掃到這個 package）────── */
const S = {
    content: {
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        padding: 12,
        /* 手機底部 nav 會蓋住最後一顆按鈕，預留 safe-area（§6.1）。 */
        paddingBottom: 'calc(16px + env(safe-area-inset-bottom))',
        minHeight: 0,
        overflowY: 'auto',
    },
    headActions: { display: 'flex', alignItems: 'center', gap: 8, flex: 'none' },
    section: { display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 },
    sectionHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' },
    sectionTitle: {
        fontFamily: 'var(--ak-font-mono)',
        fontSize: 10,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: 'var(--ak-color-text-tertiary)',
    },
    toolbar: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
    /**
     * 窄版（桌機轉卡片同一條界線）：工具列改直向排列，每個控制項佔滿寬度。
     * 原本橫排在 390 寬會擠爆：標籤「站別」被斷成兩行、兩個 select 前後錯位。
     */
    toolbarStacked: { display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 8 },
    sectionHeadStacked: { flexDirection: 'column', alignItems: 'stretch', gap: 6 },
    /**
     * 篩選列（窄版）：站別標籤與下拉排成「標籤一欄、下拉吃其餘寬」的兩欄 grid。
     * 用 flex + `space-between` 時，390 寬仍會把「篩選站別」擠成兩行、下拉上下不對齊；
     * grid 的 `max-content` 欄讓標籤寬度固定，兩欄 `alignItems:'center'` 永遠同一條中線。
     */
    fieldRow: {
        display: 'grid',
        gridTemplateColumns: 'max-content minmax(0, 1fr)',
        alignItems: 'center',
        gap: 8,
        minWidth: 0,
    },
    /** 「標籤＋控制項」一列的寬版寫法：不換行，標籤不壓縮。 */
    fieldRowInline: { display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'nowrap', minWidth: 0 },
    labelText: { flex: 'none', whiteSpace: 'nowrap' },
    card: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        minWidth: 0,
        padding: 10,
        border: '1px solid var(--ak-color-border)',
        borderRadius: 'var(--ak-radius-md)',
    },
    cardLabel: {
        fontFamily: 'var(--ak-font-mono)',
        fontSize: 10,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: 'var(--ak-color-text-tertiary)',
    },
    cardBig: {
        fontFamily: 'var(--ak-font-mono)',
        fontSize: 18,
        lineHeight: 1.2,
        color: 'var(--ak-color-text)',
        fontVariantNumeric: 'tabular-nums',
    },
    cardSub: { fontSize: 10.5, lineHeight: 1.45, color: 'var(--ak-color-text-tertiary)' },
    cardRow: { display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 },
    tableWrap: { width: '100%', overflowX: 'auto', border: '1px solid var(--ak-color-border)', borderRadius: 'var(--ak-radius-md)' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: 12 },
    th: {
        padding: '6px 8px',
        textAlign: 'left',
        whiteSpace: 'nowrap',
        fontFamily: 'var(--ak-font-mono)',
        fontSize: 10,
        fontWeight: 500,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: 'var(--ak-color-text-tertiary)',
        borderBottom: '1px solid var(--ak-color-border)',
    },
    td: {
        padding: '6px 8px',
        whiteSpace: 'nowrap',
        color: 'var(--ak-color-text)',
        borderBottom: '1px solid var(--ak-color-border)',
        fontVariantNumeric: 'tabular-nums',
    },
    cardItem: {
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        width: '100%',
        padding: 10,
        textAlign: 'left',
        borderRadius: 'var(--ak-radius-md)',
        border: '1px solid var(--ak-color-border)',
        background: 'transparent',
        color: 'inherit',
        cursor: 'pointer',
    },
    cardItemHead: { display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'space-between' },
    cardItemStats: { display: 'flex', flexWrap: 'wrap', gap: 10, fontSize: 11 },
    stat: { display: 'flex', alignItems: 'baseline', gap: 4 },
    statLabel: { fontSize: 10, color: 'var(--ak-color-text-tertiary)' },
    statValue: { fontFamily: 'var(--ak-font-mono)', fontVariantNumeric: 'tabular-nums', color: 'var(--ak-color-text)' },
    error: { margin: 0, fontSize: 11, color: 'var(--ak-color-danger)' },
    overlay: {
        position: 'fixed',
        inset: 0,
        zIndex: 60,
        display: 'flex',
        justifyContent: 'flex-end',
        background: 'color-mix(in srgb, var(--ak-color-background) 55%, #000)',
    },
    drawer: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        width: '100%',
        maxWidth: 440,
        background: 'var(--ak-color-card)',
        borderLeft: '1px solid var(--ak-color-border)',
        paddingBottom: 'calc(12px + env(safe-area-inset-bottom))',
    },
    drawerHead: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
        padding: '10px 12px',
        borderBottom: '1px solid var(--ak-color-border)',
        flex: 'none',
    },
    drawerBody: { display: 'flex', flexDirection: 'column', gap: 12, padding: 12, overflowY: 'auto', minHeight: 0 },
    detailGrid: { display: 'grid', gap: 8, gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' },
    resultList: { display: 'flex', flexDirection: 'column', gap: 4, margin: 0, padding: 0, listStyle: 'none' },
    resultItem: {
        display: 'flex',
        alignItems: 'baseline',
        gap: 6,
        padding: '4px 8px',
        border: '1px solid var(--ak-color-border)',
        borderRadius: 'var(--ak-radius-sm)',
        fontSize: 11.5,
    },
};
/** 批次表 7 欄（§6.1）；桌機列與手機卡片吃同一份欄位（T11 `EntityTable`）。 */
export function RunsTable({ runs, selectedId, now, onSelect }) {
    const columns = [
        {
            id: 'at',
            label: '時間',
            width: '20%',
            sortValue: (run) => run.at,
            render: (run) => _jsx("span", { className: "cui-mono", children: formatStamp(run.at, now) }),
        },
        {
            id: 'source',
            label: '來源',
            width: '12%',
            sortValue: (run) => run.source,
            render: (run) => (_jsx("span", { className: "cui-badge", "data-run-source": run.source, children: RUN_SOURCE_LABELS[run.source] ?? run.source })),
        },
        {
            id: 'service',
            label: '站別',
            width: '12%',
            sortValue: (run) => run.service,
            render: (run) => _jsx("span", { className: `cui-badge cui-badge--${run.service}`, children: SERVICE_LABELS[run.service] }),
        },
        {
            id: 'requested',
            label: '要求',
            width: '10%',
            align: 'end',
            sortValue: (run) => run.requested,
            render: (run) => run.requested,
        },
        {
            id: 'created',
            label: '成功',
            width: '14%',
            align: 'end',
            sortValue: (run) => run.created,
            render: (run) => (_jsxs(_Fragment, { children: [run.created, run.failed > 0 ? (_jsxs("span", { style: { color: 'var(--ak-color-danger)' }, title: `失敗 ${run.failed} 顆`, children: [' ', "\u2212", run.failed] })) : null] })),
        },
        {
            id: 'duration',
            label: '耗時',
            width: '12%',
            align: 'end',
            sortValue: (run) => run.durationMs,
            render: (run) => formatDuration(run.durationMs),
        },
        {
            id: 'pool',
            label: '池子結餘',
            width: '20%',
            align: 'end',
            sortValue: (run) => run.poolAfter.ek21 ?? null,
            render: (run) => (_jsxs("span", { className: "cui-mono", title: "ek21\uFF0FUT\uFF1B\u2014 \u8868\u793A\u90A3\u6279\u6C92\u6709\u91CF\u5230\u6C60\u5B50\u5927\u5C0F\uFF08\u4E0D\u662F 0\uFF09", style: { fontFamily: 'var(--ak-font-mono)' }, children: [countText(run.poolAfter.ek21), "\uFF0F", countText(run.poolAfter.ut)] })),
        },
    ];
    return (_jsx(EntityTable, { rows: runs, columns: columns, rowKey: (run) => run.id, testId: "admin-ops-run-table", rowTestId: "admin-ops-run-row", rowSelected: (run) => run.id === selectedId, onRowClick: onSelect, rowClassName: (run) => (run.id === selectedId ? 'is-selected' : ''), rowAttrs: (run) => ({ 'data-run-id': run.id, 'data-source': run.source }) }));
}
/** 點一列後的抽屜：逐顆結果＋失敗原因（§3 A4）。 */
export function RunDetailDrawer({ run, onClose }) {
    const results = run.results;
    const rows = Array.isArray(results) ? results : [];
    return (_jsx("div", { style: S.overlay, role: "dialog", "aria-modal": "true", "aria-label": `批次 ${run.id}`, "data-testid": "admin-ops-drawer", onClick: (event) => {
            if (event.target === event.currentTarget)
                onClose();
        }, children: _jsxs("div", { style: S.drawer, "data-testid": "admin-ops-drawer-panel", children: [_jsxs("header", { style: S.drawerHead, children: [_jsxs("div", { style: { display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }, children: [_jsx("span", { className: "cui-panel-title", children: "\u6279\u6B21\u7D50\u679C" }), _jsxs("span", { className: "cui-hint", style: { fontFamily: 'var(--ak-font-mono)' }, children: [formatStamp(run.at), " \u00B7 ", RUN_SOURCE_LABELS[run.source] ?? run.source, " \u00B7 ", SERVICE_LABELS[run.service]] })] }), _jsx("button", { type: "button", className: "cui-btn cui-btn--sm", onClick: onClose, "data-testid": "admin-ops-drawer-close", children: "\u95DC\u9589" })] }), _jsxs("div", { style: S.drawerBody, children: [_jsxs("div", { style: S.detailGrid, "data-testid": "admin-ops-drawer-summary", children: [_jsxs("span", { style: S.stat, children: [_jsx("span", { style: S.statLabel, children: "\u8981\u6C42" }), _jsx("span", { style: S.statValue, children: run.requested })] }), _jsxs("span", { style: S.stat, children: [_jsx("span", { style: S.statLabel, children: "\u6210\u529F" }), _jsx("span", { style: S.statValue, children: run.created })] }), _jsxs("span", { style: S.stat, children: [_jsx("span", { style: S.statLabel, children: "\u5931\u6557" }), _jsx("span", { style: { ...S.statValue, color: run.failed > 0 ? 'var(--ak-color-danger)' : undefined }, children: run.failed })] }), _jsxs("span", { style: S.stat, children: [_jsx("span", { style: S.statLabel, children: "\u8017\u6642" }), _jsx("span", { style: S.statValue, children: formatDuration(run.durationMs) })] }), _jsxs("span", { style: S.stat, children: [_jsx("span", { style: S.statLabel, children: "\u6253\u5230\u7AD9\u65B9" }), _jsx("span", { style: S.statValue, children: run.hitSite ? '是' : '否（本地擋掉）' })] }), _jsxs("span", { style: S.stat, children: [_jsx("span", { style: S.statLabel, children: "\u6C60\u5B50\u7D50\u9918" }), _jsxs("span", { style: S.statValue, children: ["ek21 ", countText(run.poolAfter.ek21), "\uFF0FUT ", countText(run.poolAfter.ut)] })] })] }), _jsxs("div", { style: S.section, children: [_jsx("span", { style: S.sectionTitle, children: "\u5931\u6557\u539F\u56E0" }), run.errors.length > 0 ? (_jsx("ul", { style: S.resultList, "data-testid": "admin-ops-drawer-errors", children: run.errors.slice(0, 3).map((reason, index) => (_jsxs("li", { style: { ...S.resultItem, color: 'var(--ak-color-danger)' }, children: [_jsx("span", { "aria-hidden": "true", children: "\u2717" }), _jsx("span", { children: reason })] }, `${reason}:${index}`))) })) : (_jsx("p", { className: "cui-hint", "data-testid": "admin-ops-drawer-no-errors", children: "\u9019\u6279\u6C92\u6709\u5931\u6557\u539F\u56E0\u3002" }))] }), _jsxs("div", { style: S.section, children: [_jsx("span", { style: S.sectionTitle, children: "\u9010\u9846\u7D50\u679C" }), rows.length > 0 ? (_jsx("ul", { style: S.resultList, "data-testid": "admin-ops-drawer-results", children: rows.map((item, index) => (_jsxs("li", { style: { ...S.resultItem, color: item.ok ? 'var(--ak-color-text)' : 'var(--ak-color-danger)' }, "data-testid": "admin-ops-drawer-result", "data-ok": item.ok, children: [_jsx("span", { "aria-hidden": "true", children: item.ok ? '✓' : '✗' }), _jsx("span", { className: "cui-mono", children: item.username }), _jsx("span", { className: "cui-muted", style: { marginLeft: 'auto', textAlign: 'right' }, children: item.ok ? '可用' : readableRegisterReason(item.reason) })] }, `${item.username}:${index}`))) })) : (_jsx("p", { className: "cui-hint", "data-testid": "admin-ops-drawer-no-results", children: "\u9019\u7B46 run \u53EA\u6709\u5F59\u7E3D\uFF08\u6C92\u6709\u9010\u9846 `results`\uFF09\uFF1B\u8981\u770B\u55AE\u9846\u6210\u6557\u8ACB\u5230\u5E33\u865F\u6C60\u5C0D\u5E33\u865F\u540D\u3002" }))] }), _jsx("p", { className: "cui-hint", style: { margin: 0 }, children: "\u4E0D\u63D0\u4F9B\u300C\u91CD\u8DD1\u9019\u4E00\u8F2A\u300D\uFF1A\u91CD\u8DD1\u8ACB\u8D70\u6392\u7A0B\u6216\u5E33\u865F\u6C60\u7684\u300C\u8A3B\u518A\u300D\uFF0C\u90A3\u88E1\u6709 20 \u79D2\u7BC0\u6D41\uFF08\u00A73 A4\uFF09\u3002" })] })] }) }));
}
/* ── 區塊：系統 4 卡 ───────────────────────────────────────────────── */
function StatLine({ label, value }) {
    return (_jsxs("span", { style: S.stat, children: [_jsx("span", { style: S.statLabel, children: label }), _jsx("span", { style: S.statValue, children: value })] }));
}
export function SystemCards({ status, cooldownUntil, now, }) {
    const secondsLeft = cooldownSecondsLeft(cooldownUntil, now);
    const relay = status?.relay;
    const scheduler = status?.scheduler;
    const sites = status?.sites ?? [];
    return (_jsxs("div", { "data-testid": "admin-ops-system", style: { display: 'grid', gap: 8, gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }, children: [_jsxs("div", { style: S.card, "data-testid": "admin-ops-card-cooldown", children: [_jsx("span", { style: S.cardLabel, children: "\u8A3B\u518A\u51B7\u537B" }), _jsx("span", { style: { ...S.cardBig, color: secondsLeft > 0 ? 'var(--ak-color-warning)' : 'var(--ak-color-success)' }, children: status === null ? '—' : secondsLeft > 0 ? `${secondsLeft} 秒` : '可註冊' }), _jsxs("span", { style: S.cardSub, children: ["\u4E0A\u6B21\u6279\u6B21 ", formatStamp(status?.registration.lastBatchAt, now)] }), _jsx("span", { style: S.cardSub, children: secondsLeft > 0 ? '冷卻中，「註冊一批」已鎖住' : '按鈕目前可按' })] }), _jsxs("div", { style: S.card, "data-testid": "admin-ops-card-relay", children: [_jsx("span", { style: S.cardLabel, children: "relay" }), _jsxs("span", { style: S.cardRow, children: [_jsx("span", { className: `cui-health-dot ${relay === undefined ? '' : !relay.configured ? 'cui-health-dot--idle' : relay.reachable ? 'cui-health-dot--online' : 'cui-health-dot--down'}`, "aria-hidden": "true" }), _jsx("span", { style: S.cardBig, children: relay === undefined ? '—' : reachLabel(relay.configured, relay.reachable) })] }), _jsxs("span", { style: S.cardSub, children: ["\u5EF6\u9072 ", relay === undefined ? '—' : isNum(relay.latencyMs) ? `${relay.latencyMs} ms` : '未量測'] }), _jsx("span", { style: S.cardSub, children: relay !== undefined && relay.forbiddenBackoffSec > 0
                            ? `站方冷卻 ${relay.forbiddenBackoffSec} 秒`
                            : '無 429 backoff' }), _jsxs("span", { style: S.cardSub, children: ["\u6AA2\u67E5 ", formatStamp(relay?.checkedAt, now)] })] }), _jsxs("div", { style: S.card, "data-testid": "admin-ops-card-sites", children: [_jsx("span", { style: S.cardLabel, children: "\u7AD9\u65B9\u53EF\u9054\u6027" }), enabledServices().map((service) => {
                        const site = sites.find((item) => item.service === service);
                        return (_jsxs("span", { style: S.cardRow, "data-testid": "admin-ops-site-row", "data-service": service, children: [_jsx("span", { className: `cui-health-dot ${site === undefined ? '' : site.reachable ? 'cui-health-dot--online' : 'cui-health-dot--down'}`, "aria-hidden": "true" }), _jsx("span", { style: { ...S.statValue, fontSize: 12 }, children: SERVICE_LABELS[service] }), _jsxs("span", { style: S.cardSub, children: [site === undefined ? '未檢查' : site.reachable ? '可達' : '不可達', site !== undefined && isNum(site.latencyMs) ? ` · ${site.latencyMs} ms` : ''] })] }, service));
                    }), _jsx("span", { style: S.cardSub, children: "\u53EA\u8B80\uFF0C5 \u5206\u9418\u5167\u4E0D\u91CD\u6253" })] }), _jsxs("div", { style: S.card, "data-testid": "admin-ops-card-scheduler", children: [_jsx("span", { style: S.cardLabel, children: "\u6392\u7A0B" }), _jsxs("span", { style: S.cardRow, children: [_jsx("span", { className: `cui-health-dot ${scheduler === undefined ? '' : RESULT_DOT[scheduler.lastResult]}`, "aria-hidden": "true" }), _jsx("span", { style: S.cardBig, children: scheduler === undefined ? '—' : (SCHEDULER_RESULT_LABELS[scheduler.lastResult] ?? scheduler.lastResult) })] }), _jsxs("span", { style: S.cardSub, children: ["\u4E0A\u6B21\u57F7\u884C ", formatStamp(scheduler?.lastRunAt, now), scheduler?.lastRunAt ? `（${formatAgo(scheduler.lastRunAt, now)}）` : ''] }), _jsxs("span", { style: S.cardSub, children: ["\u4F86\u6E90 ", scheduler === undefined ? '—' : (SCHEDULER_SOURCE_LABELS[scheduler.source] ?? scheduler.source)] })] })] }));
}
export function RunsPage({ client } = {}) {
    const narrow = useNarrowLayout();
    const [runs, setRuns] = useState([]);
    const [runsLoaded, setRunsLoaded] = useState(false);
    const [runsLoading, setRunsLoading] = useState(false);
    const [runsError, setRunsError] = useState(null);
    const [nextCursor, setNextCursor] = useState(null);
    const [serviceFilter, setServiceFilter] = useState('all');
    const [selectedId, setSelectedId] = useState(null);
    const [system, setSystem] = useState(null);
    const [systemError, setSystemError] = useState(null);
    const [checking, setChecking] = useState(false);
    const [checkedAt, setCheckedAt] = useState(null);
    const [cooldownUntil, setCooldownUntil] = useState(null);
    const [clock, setClock] = useState(() => Date.now());
    const [registerService, setRegisterService] = useState('ek21');
    const [registerCount, setRegisterCount] = useState(1);
    const [registering, setRegistering] = useState(false);
    const [registerError, setRegisterError] = useState(null);
    /** 逐顆結果（新的在前）；**跨批累積**——後面一批失敗不能把前面成功的結果洗掉。 */
    const [registerBatches, setRegisterBatches] = useState([]);
    const registerBatchSeq = useRef(0);
    /** 連點防護（state 更新是非同步的，用 ref 才擋得住同一輪的第二次點擊）。 */
    const checkingRef = useRef(false);
    const registeringRef = useRef(false);
    /** 分頁 cursor 放 ref：避免 `loadRuns` 因為 cursor 變動而重建、連帶觸發重新載入。 */
    const cursorRef = useRef(null);
    const canListRuns = typeof client?.listRuns === 'function';
    const canCheckSystem = typeof client?.system === 'function';
    const canRegister = typeof client?.registerAccountPool === 'function';
    const loadRuns = useCallback(async (mode) => {
        if (client?.listRuns === undefined) {
            setRunsLoaded(true);
            return;
        }
        setRunsLoading(true);
        setRunsError(null);
        try {
            const page = await client.listRuns({
                limit: RUN_PAGE_SIZE,
                ...(serviceFilter === 'all' ? {} : { service: serviceFilter }),
                ...(mode === 'append' && cursorRef.current !== null ? { cursor: cursorRef.current } : {}),
            });
            cursorRef.current = page.nextCursor ?? null;
            setNextCursor(page.nextCursor ?? null);
            setRuns((prev) => {
                if (mode === 'replace')
                    return page.runs;
                const seen = new Set(prev.map((run) => run.id));
                return [...prev, ...page.runs.filter((run) => !seen.has(run.id))];
            });
        }
        catch (error) {
            setRunsError(errorMessage(error));
        }
        finally {
            setRunsLoaded(true);
            setRunsLoading(false);
        }
    }, [client, serviceFilter]);
    /** `refresh:false` = 吃後端 60 秒快取；只有人工按鈕才 `true`（§4.3-3，不每次載入都打站方）。 */
    const loadSystem = useCallback(async (refresh) => {
        const systemFn = client?.system;
        if (systemFn === undefined)
            return;
        if (checkingRef.current)
            return;
        checkingRef.current = true;
        setChecking(true);
        setSystemError(null);
        try {
            // 型別上 `system()` 沒有參數；多帶一個 `{refresh}` 對舊實作無害，對新實作才有意義。
            const request = systemFn;
            const next = await request.call(client, { refresh });
            setSystem(next);
            setCheckedAt(Date.now());
            const serverUntil = next.registration.throttleRemainingMs > 0 ? next.checkedAt + next.registration.throttleRemainingMs : 0;
            // 取兩邊最大值：後端快取可能比本地剛起算的 20 秒還舊，不能因此把倒數歸零。
            setCooldownUntil((prev) => Math.max(prev ?? 0, serverUntil) || null);
        }
        catch (error) {
            setSystemError(errorMessage(error));
        }
        finally {
            checkingRef.current = false;
            setChecking(false);
        }
    }, [client]);
    // 換篩選就重抓第一頁（cursor 歸零）。
    useEffect(() => {
        cursorRef.current = null;
        setNextCursor(null);
        void loadRuns('replace');
    }, [loadRuns]);
    useEffect(() => {
        void loadSystem(false);
    }, [loadSystem]);
    // 倒數每秒跳一次；跳完就自我停掉，不在背景一直 tick。
    useEffect(() => {
        setClock(Date.now());
        if (cooldownUntil === null || cooldownUntil <= Date.now())
            return;
        const timer = setInterval(() => {
            const now = Date.now();
            setClock(now);
            if (cooldownUntil <= now)
                clearInterval(timer);
        }, 1000);
        return () => clearInterval(timer);
    }, [cooldownUntil]);
    // Esc 關抽屜。
    useEffect(() => {
        if (selectedId === null)
            return;
        const onKeyDown = (event) => {
            if (event.key === 'Escape')
                setSelectedId(null);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [selectedId]);
    const secondsLeft = cooldownSecondsLeft(cooldownUntil, clock);
    const cooling = secondsLeft > 0;
    const registerBlocked = cooling || registering || !canRegister;
    const selectedRun = useMemo(() => runs.find((run) => run.id === selectedId) ?? null, [runs, selectedId]);
    const handleRegister = async () => {
        // 冷卻中／已在送／這個 client 沒這個方法 → 直接不發請求（§6.2-4 要 Network 0 筆）。
        if (registeringRef.current || cooling || client?.registerAccountPool === undefined)
            return;
        registeringRef.current = true;
        setRegistering(true);
        setRegisterError(null);
        const sentAt = Date.now();
        try {
            const result = await client.registerAccountPool({ service: registerService, count: registerCount });
            // 逐顆結果留在畫面上：summary 只算數字、details 回答「哪幾顆、為什麼」。
            const batch = {
                key: (registerBatchSeq.current += 1),
                at: sentAt,
                service: result.service,
                requested: result.requested,
                created: result.created,
                results: result.results,
            };
            setRegisterBatches((prev) => [batch, ...prev].slice(0, 5));
            // 不等 system 回來就先鎖 20 秒，堵住「按下到回應」之間的空窗。
            setCooldownUntil(Math.max(sentAt + POOL_REGISTER_GAP_MS, Date.now()));
            // 批次紀錄要立刻看到這批；system 只吃快取（refresh:false），不因為按了按鈕又去打站方。
            await Promise.all([loadRuns('replace'), loadSystem(false)]);
        }
        catch (error) {
            setRegisterError(errorMessage(error));
        }
        finally {
            registeringRef.current = false;
            setRegistering(false);
        }
    };
    return (_jsxs("section", { className: "cui-panel", "data-testid": "admin-page-ops", "aria-label": "\u4EFB\u52D9\u8207\u7CFB\u7D71", children: [_jsxs("header", { className: "cui-panel-head", children: [_jsx("h2", { className: "cui-panel-title", children: "\u4EFB\u52D9\u8207\u7CFB\u7D71" }), _jsxs("div", { style: S.headActions, children: [_jsx("span", { className: "cui-count", "data-testid": "admin-ops-run-count", children: !runsLoaded ? '載入中…' : runs.length > 0 ? `${runs.length} 批` : '—' }), _jsx("button", { type: "button", className: "cui-btn cui-btn--sm", onClick: () => void loadSystem(true), disabled: checking || !canCheckSystem, "aria-busy": checking, title: "\u5F37\u5236\u91CD\u6253 relay \u8207\u5169\u7AD9\uFF08\u5E73\u5E38\u5403 60 \u79D2\u5FEB\u53D6\uFF09", "data-testid": "admin-ops-recheck", children: checking ? '檢查中…' : '重新檢查' })] })] }), _jsxs("div", { style: S.content, children: [client === undefined ? (_jsx("p", { className: "cui-hint", "data-testid": "admin-ops-unwired", style: { margin: 0 }, children: "\u7BA1\u7406\u8CC7\u6599\u66AB\u6642\u6C92\u6709\u63A5\u901A\uFF0C\u8ACB\u7A0D\u5F8C\u91CD\u65B0\u6574\u7406\u3002" })) : null, _jsxs("div", { style: S.toolbar, "data-testid": "admin-ops-checked-at", children: [_jsx("span", { className: "cui-hint", children: checkedAt === null ? '尚未檢查 relay／站方' : `最後檢查 ${formatStamp(checkedAt, clock)}（${formatAgo(checkedAt, clock)}）` }), systemError !== null ? (_jsx("span", { style: S.error, role: "alert", "data-testid": "admin-ops-system-error", children: systemError })) : null, system === null && canCheckSystem && !checking && systemError === null ? (_jsx("span", { className: "cui-hint", children: "\uFF08\u7B49\u7B2C\u4E00\u6B21\u6AA2\u67E5\u56DE\u5831\uFF09" })) : null] }), _jsxs("div", { style: S.section, "data-testid": "admin-ops-runs", children: [_jsxs("div", { style: narrow ? { ...S.sectionHead, ...S.sectionHeadStacked } : S.sectionHead, children: [_jsx("span", { style: S.sectionTitle, children: "\u8A3B\u518A\u6279\u6B21" }), _jsxs("label", { className: "cui-hint", style: narrow ? { ...S.fieldRow, width: '100%' } : S.fieldRowInline, children: [_jsx("span", { style: S.labelText, children: "\u7BE9\u9078\u7AD9\u5225" }), _jsxs("select", { className: "cui-select", style: narrow ? { width: '100%', minWidth: 0, height: 34 } : undefined, value: serviceFilter, onChange: (event) => setServiceFilter(event.target.value), "data-testid": "admin-ops-filter-service", disabled: !canListRuns, children: [_jsx("option", { value: "all", children: "\u5168\u90E8" }), _jsx("option", { value: "ek21", children: "ek21" }), _jsx("option", { value: "ut", children: "UT" })] })] })] }), _jsxs("div", { style: narrow ? S.toolbarStacked : S.toolbar, "data-testid": "admin-ops-register-toolbar", children: [narrow ? _jsx("span", { style: { ...S.statLabel }, children: "\u8A3B\u518A\u7AD9\u5225" }) : null, _jsx("select", { className: "cui-select", style: narrow ? { width: '100%', height: 34 } : undefined, value: registerService, onChange: (event) => setRegisterService(event.target.value), "aria-label": "\u8A3B\u518A\u5230\u54EA\u500B\u7AD9", "data-testid": "admin-ops-register-service", disabled: registering, children: enabledServices().map((service) => (_jsx("option", { value: service, children: SERVICE_LABELS[service] }, service))) }), _jsx("div", { style: { display: 'flex', gap: 3, minWidth: 0 }, role: "group", "aria-label": "\u4E00\u6B21\u8A3B\u518A\u5E7E\u9846", children: Array.from({ length: MAX_POOL_REGISTER }, (_, index) => index + 1).map((count) => (_jsx("button", { type: "button", className: `cui-pool-count${registerCount === count ? ' is-active' : ''}`, "aria-pressed": registerCount === count, onClick: () => setRegisterCount(count), disabled: registering, "data-testid": `admin-ops-register-count-${count}`, children: count }, count))) }), _jsx("button", { type: "button", className: "cui-btn cui-btn--primary cui-btn--sm", style: narrow ? { minHeight: 40 } : undefined, onClick: () => void handleRegister(), disabled: registerBlocked, "aria-busy": registering, title: cooling
                                            ? `兩批註冊之間要隔 ${POOL_REGISTER_GAP_MS / 1000} 秒，還要等 ${secondsLeft} 秒`
                                            : `經台灣出口註冊 ${registerCount} 顆 ${SERVICE_LABELS[registerService]} 帳號`, "data-testid": "admin-ops-register", children: registering ? '註冊中…' : cooling ? `冷卻 ${secondsLeft} 秒` : '註冊一批' }), cooling ? (_jsxs("span", { className: "cui-hint", role: "timer", "data-testid": "admin-ops-cooldown", children: ["\u9084\u8981 ", secondsLeft, " \u79D2\u624D\u80FD\u518D\u9001"] })) : null] }), registerError !== null ? (_jsx("p", { style: S.error, role: "alert", "data-testid": "admin-ops-register-error", children: readableRegisterReason(registerError) })) : null, _jsx(RegisterBatches, { batches: registerBatches }), runsError !== null ? (_jsxs("div", { style: { display: 'flex', alignItems: 'center', gap: 8 }, children: [_jsx("p", { style: S.error, role: "alert", "data-testid": "admin-ops-runs-error", children: runsError }), _jsx("button", { type: "button", className: "cui-btn cui-btn--sm", onClick: () => void loadRuns('replace'), "data-testid": "admin-ops-runs-retry", children: "\u91CD\u8A66" })] })) : null, !canListRuns && client !== undefined ? (_jsx("p", { className: "cui-hint", "data-testid": "admin-ops-runs-unwired", style: { margin: 0 }, children: "\u6279\u6B21\u7D00\u9304\u66AB\u6642\u8B80\u4E0D\u5230\uFF0C\u8ACB\u7A0D\u5F8C\u91CD\u65B0\u6574\u7406\u3002" })) : null, runsLoading && !runsLoaded ? (_jsx("div", { className: "cui-entity-skeleton", "data-testid": "admin-ops-runs-loading", "data-state": "loading", role: "status", "aria-busy": "true", children: _jsx("span", { className: "cui-hint", children: "\u8F09\u5165\u6279\u6B21\u7D00\u9304\u2026" }) })) : null, runsLoaded && runs.length === 0 && runsError === null ? (_jsxs("div", { className: "cui-empty", "data-testid": "admin-ops-runs-empty", style: { alignItems: 'flex-start' }, children: [_jsx("span", { children: "\u9084\u6C92\u6709\u6279\u6B21\u7D00\u9304\u3002" }), _jsx("span", { className: "cui-hint", children: "\u5148\u6309\u4E0A\u9762\u300C\u8A3B\u518A\u4E00\u6279\u300D\uFF0C\u6216\u7B49\u6392\u7A0B\u8DD1\u4E00\u8F2A\u518D\u56DE\u4F86\u770B\u3002" })] })) : null, runs.length > 0 ? (_jsx(RunsTable, { runs: runs, selectedId: selectedId, now: clock, onSelect: (run) => setSelectedId(run.id) })) : null, nextCursor !== null ? (_jsx("button", { type: "button", className: "cui-btn cui-btn--sm", onClick: () => void loadRuns('append'), disabled: runsLoading, "data-testid": "admin-ops-runs-more", children: runsLoading ? '載入中…' : '載入更舊' })) : null] }), _jsxs("div", { style: S.section, children: [_jsxs("div", { style: S.sectionHead, children: [_jsx("span", { style: S.sectionTitle, children: "\u7CFB\u7D71\u72C0\u614B" }), _jsx("span", { className: "cui-hint", children: checkedAt === null ? '—' : `檢查於 ${formatStamp(checkedAt, clock)}` })] }), _jsx(SystemCards, { status: system, cooldownUntil: cooldownUntil, now: clock }), !canCheckSystem && client !== undefined ? (_jsx("p", { className: "cui-hint", "data-testid": "admin-ops-system-unwired", style: { margin: 0 }, children: "\u7CFB\u7D71\u72C0\u614B\u66AB\u6642\u8B80\u4E0D\u5230\uFF0C\u8ACB\u7A0D\u5F8C\u91CD\u65B0\u6574\u7406\u3002" })) : null] })] }), selectedRun !== null ? (_jsx(RunDetailDrawer, { run: selectedRun, onClose: () => setSelectedId(null) })) : null] }));
}
//# sourceMappingURL=RunsPage.js.map